import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupModal({ open, onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.includes("@")) {
            toast.error("Please enter your name and a valid email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/waitlist`, { name: name.trim(), email: email.trim() });
            setDone(true);
            toast.success("You're in — watch your inbox.");
        } catch (err) {
            toast.error("Something went wrong — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
                    onClick={onClose}
                    data-testid="signup-modal-overlay"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-md bg-cream rounded-t-[2rem] sm:rounded-[2rem] p-7 pb-10"
                        data-testid="signup-modal"
                    >
                        <div className="flex items-center justify-between">
                            <LogoMark size={40} />
                            <button
                                onClick={onClose}
                                className="w-10 h-10 rounded-full bg-black/5 grid place-items-center hover:bg-black/10 transition-colors duration-200"
                                aria-label="Close"
                                data-testid="signup-modal-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {done ? (
                            <div className="text-center pt-8" data-testid="signup-success">
                                <span className="mx-auto w-16 h-16 rounded-full bg-teal grid place-items-center">
                                    <Check size={30} strokeWidth={3} className="text-white" />
                                </span>
                                <h3 className="mt-5 font-extrabold text-2xl tracking-tight">You're in.</h3>
                                <p className="mt-2 text-black/55 text-[15px]">
                                    We'll email you your 7-day free trial invite as soon as Med Check Alarm opens in your region.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="pt-6 space-y-5" data-testid="signup-form">
                                <h3 className="font-extrabold text-2xl tracking-tight">Get started free</h3>
                                <div>
                                    <label htmlFor="su-name" className="text-xs font-bold uppercase tracking-wider text-black/45">Full name</label>
                                    <input
                                        id="su-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Margaret Walker"
                                        className="mt-1.5 w-full h-13 h-12 px-4 rounded-2xl border border-black/12 bg-white outline-none focus:border-teal text-[16px] transition-colors duration-200"
                                        data-testid="signup-name-input"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="su-email" className="text-xs font-bold uppercase tracking-wider text-black/45">Email address</label>
                                    <input
                                        id="su-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="mt-1.5 w-full h-12 px-4 rounded-2xl border border-black/12 bg-white outline-none focus:border-teal text-[16px] transition-colors duration-200"
                                        data-testid="signup-email-input"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-14 rounded-full bg-teal text-white font-bold text-lg transition-all duration-200 hover:bg-teal-dark active:scale-[0.98] disabled:opacity-60 flex items-center justify-center"
                                    data-testid="signup-submit-button"
                                >
                                    {loading ? <Loader2 size={20} className="animate-spin" /> : "Start my free trial"}
                                </button>
                                <p className="text-center text-black/45 text-[13px]">7-day free trial · Cancel anytime</p>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
